import { getLocalApiUrl, authFetch } from '../config/api'

export const PATRON_SEARCH_MIN_CHARS = 2
export const PATRON_SEARCH_DEBOUNCE_MS = 280

/** Club membership number from GetProfile row (staff-facing id). */
export function patronMembershipNumber(p) {
  const mn = String(p?.MembershipNumber || p?.membership_number || '').trim()
  return mn || null
}

export function patronSearchLabel(p) {
  const name = [p.FN, p.MDN, p.LN].filter(Boolean).join(' ').trim()
  return name || patronMembershipNumber(p) || 'Unknown'
}

export function patronSearchKey(p) {
  return p.MasterProfileID || patronMembershipNumber(p) || p.MID || patronSearchLabel(p)
}

/** True when query looks like a club membership number (not a person name). */
export function looksLikeMembershipNumber(q) {
  const s = String(q || '').trim()
  if (!s || /\s/.test(s)) return false
  if (/^\d+$/.test(s)) return true
  if (/^[A-Za-z]?\d{3,}$/.test(s)) return true
  if (/^[A-Za-z0-9_-]{4,}$/.test(s) && /\d/.test(s)) return true
  return false
}

/**
 * Build GetProfile bodies.
 * ID-like input → MembershipNumber only (never MemberID / player id).
 * Name input → first/last name only.
 */
export function buildProfileSearchBodies(query) {
  const q = String(query || '').trim()
  if (q.length < PATRON_SEARCH_MIN_CHARS) return []
  const base = { PageNumber: '1', PageSize: '12' }
  if (looksLikeMembershipNumber(q)) {
    return [{ ...base, MembershipNumber: q }]
  }
  const parts = q.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return [{ ...base, Firstname: parts[0], Lastname: parts.slice(1).join(' ') }]
  }
  return [
    { ...base, Firstname: q },
    { ...base, Lastname: q },
  ]
}

/** Search patrons via GetProfile (name and/or membership number). Deduped, max 12. */
export async function fetchPatronMatches(query) {
  const bodies = buildProfileSearchBodies(query)
  if (bodies.length === 0) return []
  const idSearch = looksLikeMembershipNumber(query)
  const wantMn = idSearch ? String(query).trim() : null
  const responses = await Promise.all(
    bodies.map(async (body) => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/GetProfile'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const j = await r.json()
        if (j.ResponseCode !== '0' && j.ResponseCode !== 0) return []
        return Array.isArray(j.OutputData) ? j.OutputData : []
      } catch {
        return []
      }
    })
  )
  const seen = new Set()
  const merged = []
  for (const rows of responses) {
    for (const row of rows) {
      const key = patronSearchKey(row)
      if (!key || seen.has(key)) continue
      if (wantMn) {
        const mn = patronMembershipNumber(row)
        if (!mn || mn !== wantMn) continue
      }
      seen.add(key)
      merged.push(row)
    }
  }
  return merged.slice(0, 12)
}
